import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Spin,
  Row,
  Col,
  Statistic,
  Card,
  message,
  Empty,
} from "antd";
import ReactApexChart from "react-apexcharts";
import {
  RiseOutlined,
  FallOutlined,
  DollarOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { getChartData } from "../../api/accounting";
import { getCachedData, setCachedData, CacheTTL } from "../../context/AccountingDataCache";

/**
 * ChartModal - Interactive chart modal for dashboard statistics
 *
 * Shows beautiful, animated charts using real data from the API
 * Supports: daily (24h), weekly (7 days), monthly, yearly, YTD, and custom date ranges
 */
const ChartModal = ({
  visible,
  onClose,
  chartType,
  title,
  brandIds = [],
  clientIds = [],
  dateRange = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  
  const fetchingRef = useRef(false);
  const lastFetchedCacheKeyRef = useRef(null);

  // Create cache key for this chart data
  const cacheKey = useMemo(() => {
    if (!chartType || !brandIds.length || !dateRange.startDate || !dateRange.endDate) {
      return null;
    }
    const brandsKey = brandIds.sort().join(",");
    const clientsKey = clientIds?.sort().join(",") || "all";
    return `chart-modal-${chartType}-${brandsKey}-${clientsKey}-${dateRange.startDate}-${dateRange.endDate}`;
  }, [chartType, brandIds, clientIds, dateRange.startDate, dateRange.endDate]);

  // Fetch data when modal opens or filters change
  useEffect(() => {
    if (visible && brandIds.length > 0 && dateRange.startDate && dateRange.endDate && cacheKey) {
      // Check cache first
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setChartData(cachedData);
        setLoading(false);
        lastFetchedCacheKeyRef.current = cacheKey;
        return;
      }
      
      // Only fetch if not already fetched for this key
      if (lastFetchedCacheKeyRef.current !== cacheKey) {
        loadChartData();
      }
    }
  }, [visible, cacheKey]);

  // Reset state when modal closes - but keep cache
  useEffect(() => {
    if (!visible) {
      setChartData(null);
      setError(null);
    }
  }, [visible]);

  const loadChartData = async () => {
    if (!dateRange.startDate || !dateRange.endDate || !cacheKey) {
      return;
    }

    // Prevent duplicate fetches
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await getChartData(chartType, {
        brand_ids: brandIds,
        client_ids: clientIds && clientIds.length > 0 ? clientIds : undefined,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        period: "custom",
      });

      if (response && response.series) {
        setChartData(response);
        // Cache in GLOBAL cache (5 minute TTL)
        setCachedData(cacheKey, response, CacheTTL.MEDIUM);
        lastFetchedCacheKeyRef.current = cacheKey;
      } else {
        setError("No data available for the selected period");
      }
    } catch {
      setError("Failed to load chart data. Please try again.");
      message.error("Could not load chart data");
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  // Get chart options based on type
  const getChartOptions = () => {
    if (!chartData) return {};

    const baseOptions = {
      chart: {
        fontFamily: "inherit",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        dropShadow: {
          enabled: true,
          top: 3,
          left: 2,
          blur: 4,
          opacity: 0.1,
        },
      },
      colors: chartData.colors,
      stroke: {
        curve: "smooth",
        width: chartData.type === "area" ? 3 : 2,
      },
      fill: {
        type: chartData.type === "area" ? "gradient" : "solid",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
          stops: [50, 100],
        },
      },
      dataLabels: {
        enabled: chartData.type === "donut" || chartData.type === "treemap",
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        floating: false,
        fontSize: "13px",
        markers: {
          width: 10,
          height: 10,
          radius: 2,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val) => {
            if (chartType === "growth") return `${val?.toFixed(1) || 0}%`;
            return `$${(val || 0).toLocaleString()}`;
          },
        },
      },
      grid: {
        borderColor: "#f0f0f0",
        strokeDashArray: 3,
        padding: {
          top: 0,
          right: 10,
          bottom: 0,
          left: 10,
        },
      },
      noData: {
        text: "No data available",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: "#8c8c8c",
          fontSize: "16px",
        },
      },
    };

    // Type-specific options
    if (chartData.type === "area" || chartData.type === "line") {
      return {
        ...baseOptions,
        xaxis: {
          categories: chartData.categories,
          labels: {
            style: { colors: "#8c8c8c", fontSize: "12px" },
            rotate: chartData.categories?.length > 12 ? -45 : 0,
            rotateAlways: chartData.categories?.length > 12,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: "#8c8c8c", fontSize: "12px" },
            formatter: (val) => {
              if (chartType === "growth") return `${val}%`;
              if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
              if (val >= 1000) return `$${(val / 1000).toFixed(0)}k`;
              return `$${val}`;
            },
          },
        },
      };
    }

    if (chartData.type === "bar") {
      return {
        ...baseOptions,
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "60%",
            borderRadius: 4,
            dataLabels: {
              position: "top",
            },
          },
        },
        xaxis: {
          categories: chartData.categories,
          labels: {
            style: { colors: "#8c8c8c", fontSize: "12px" },
            rotate: chartData.categories?.length > 12 ? -45 : 0,
            rotateAlways: chartData.categories?.length > 12,
          },
        },
        yaxis: {
          labels: {
            style: { colors: "#8c8c8c", fontSize: "12px" },
            formatter: (val) => {
              if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
              if (val >= 1000) return `$${(val / 1000).toFixed(0)}k`;
              return `$${val}`;
            },
          },
        },
        ...(chartData.stacked && {
          chart: { ...baseOptions.chart, stacked: true },
        }),
      };
    }

    if (chartData.type === "donut") {
      // For vendors, show dollar amounts; for invoices, show percentages
      const isVendorChart = chartType === "vendors";

      return {
        ...baseOptions,
        labels: chartData.labels,
        dataLabels: {
          enabled: true,
          formatter: (val, opts) => {
            try {
              if (!opts || opts.seriesIndex === undefined) {
                return `${(val || 0).toFixed(1)}%`;
              }

              // Get the series data - handle both array and nested array formats
              let seriesArray = chartData.series;
              if (
                Array.isArray(seriesArray) &&
                seriesArray.length > 0 &&
                Array.isArray(seriesArray[0])
              ) {
                seriesArray = seriesArray[0];
              }

              if (
                isVendorChart &&
                Array.isArray(seriesArray) &&
                seriesArray.length > 0
              ) {
                // For vendors, show percentage on slices
                const seriesIndex = opts.seriesIndex;
                const value =
                  typeof seriesArray[seriesIndex] === "number"
                    ? seriesArray[seriesIndex]
                    : parseFloat(seriesArray[seriesIndex]) || 0;
                const total = seriesArray.reduce((a, b) => {
                  const numA = typeof a === "number" ? a : parseFloat(a) || 0;
                  const numB = typeof b === "number" ? b : parseFloat(b) || 0;
                  return numA + numB;
                }, 0);
                const percentage =
                  total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
                return `${percentage}%`;
              }
              // For invoices, show percentage (val is already the percentage)
              return `${(val || 0).toFixed(1)}%`;
            } catch (error) {
              console.error("Error in dataLabels formatter:", error);
              return "0%";
            }
          },
          style: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "16px",
                  fontWeight: 600,
                },
                value: {
                  show: true,
                  fontSize: "24px",
                  fontWeight: 700,
                  formatter: (val) => {
                    if (isVendorChart) {
                      return `$${(val || 0).toLocaleString()}`;
                    }
                    return `${(val || 0).toFixed(1)}%`;
                  },
                },
                total: {
                  show: true,
                  label: isVendorChart ? "Total Spend" : "Total",
                  fontSize: "14px",
                  formatter: (w) => {
                    if (!w || !w.globals || !w.globals.seriesTotals) {
                      return isVendorChart ? "$0" : "0%";
                    }
                    const total = w.globals.seriesTotals.reduce(
                      (a, b) => a + b,
                      0
                    );
                    if (isVendorChart) {
                      return `$${(total || 0).toLocaleString()}`;
                    }
                    return `${total}%`;
                  },
                },
              },
            },
          },
        },
        tooltip: {
          ...baseOptions.tooltip,
          y: {
            formatter: (val, opts) => {
              if (!opts || !opts.w) {
                return isVendorChart
                  ? `$${(val || 0).toLocaleString()}`
                  : `${val}%`;
              }
              const { seriesIndex, w } = opts;
              if (isVendorChart) {
                if (
                  !w.globals ||
                  !w.globals.labels ||
                  !w.globals.seriesTotals
                ) {
                  return `$${(val || 0).toLocaleString()}`;
                }
                const label = w.globals.labels[seriesIndex] || "Unknown";
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                const percentage =
                  total > 0 ? ((val / total) * 100).toFixed(1) : "0.0";
                return `${label}: $${(
                  val || 0
                ).toLocaleString()} (${percentage}%)`;
              }
              return `${val}%`;
            },
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: { width: 300 },
              legend: { position: "bottom" },
            },
          },
        ],
      };
    }

    if (chartData.type === "treemap") {
      return {
        ...baseOptions,
        plotOptions: {
          treemap: {
            distributed: true,
            enableShades: true,
            shadeIntensity: 0.5,
          },
        },
        legend: { show: false },
        tooltip: {
          y: {
            formatter: (val) => `$${(val || 0).toLocaleString()}`,
          },
        },
      };
    }

    return baseOptions;
  };

  const renderSummaryCards = () => {
    if (!chartData?.summary) return null;

    const { summary } = chartData;

    // Handle cashflow summary differently
    if (chartType === "cashflow" && summary.totalInflow !== undefined) {
      return (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Total Inflow
                  </span>
                }
                value={summary.totalInflow || 0}
                precision={0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
                prefix={<RiseOutlined />}
                formatter={(val) => `$${Number(val).toLocaleString()}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Total Outflow
                  </span>
                }
                value={summary.totalOutflow || 0}
                precision={0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
                prefix={<FallOutlined />}
                formatter={(val) => `$${Number(val).toLocaleString()}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Net Cash Flow
                  </span>
                }
                value={summary.netCashflow || 0}
                precision={0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
                prefix={<DollarOutlined />}
                formatter={(val) => `$${Number(val).toLocaleString()}`}
              />
            </Card>
          </Col>
        </Row>
      );
    }

    // Handle vendor summary
    if (chartType === "vendors" && summary.topVendor !== undefined) {
      return (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Total Spend
                  </span>
                }
                value={summary.total || 0}
                precision={0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
                prefix={<DollarOutlined />}
                formatter={(val) => `$${Number(val).toLocaleString()}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Top Vendor
                  </span>
                }
                value={summary.topVendor || "N/A"}
                valueStyle={{ color: "#fff", fontSize: "20px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Vendors
                  </span>
                }
                value={summary.vendorCount || 0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
              />
            </Card>
          </Col>
        </Row>
      );
    }

    // Handle invoice summary
    if (chartType === "invoices" && summary.paid !== undefined) {
      return (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Total Invoices
                  </span>
                }
                value={summary.total || 0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>Paid</span>
                }
                value={summary.paid || 0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #faad14 0%, #f5222d 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Pending
                  </span>
                }
                value={summary.pending || 0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
              />
            </Card>
          </Col>
        </Row>
      );
    }

    // Default summary cards
    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {summary.total !== null && summary.total !== undefined && (
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>Total</span>
                }
                value={typeof summary.total === "number" ? summary.total : 0}
                precision={0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
                prefix={<DollarOutlined />}
                formatter={(val) =>
                  chartType === "invoices"
                    ? `${val}`
                    : `$${Number(val).toLocaleString()}`
                }
              />
            </Card>
          </Col>
        )}
        {summary.change !== null && summary.change !== undefined && (
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background:
                  summary.change >= 0
                    ? "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                    : "linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Change
                  </span>
                }
                value={Math.abs(summary.change)}
                precision={1}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
                prefix={
                  summary.change >= 0 ? <RiseOutlined /> : <FallOutlined />
                }
                suffix="%"
              />
            </Card>
          </Col>
        )}
        {summary.average !== null && summary.average !== undefined && (
          <Col xs={24} sm={8}>
            <Card
              size="small"
              style={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                border: "none",
              }}
            >
              <Statistic
                title={
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    Average
                  </span>
                }
                value={summary.average}
                precision={chartType === "growth" ? 1 : 0}
                valueStyle={{ color: "#fff", fontSize: "24px" }}
                prefix={<CalendarOutlined />}
                formatter={(val) =>
                  chartType === "growth"
                    ? `${val}%`
                    : `$${Number(val).toLocaleString()}`
                }
              />
            </Card>
          </Col>
        )}
      </Row>
    );
  };


  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DollarOutlined style={{ color: "#fff", fontSize: 20 }} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{title}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              {dateRange.startDate} - {dateRange.endDate}
              {brandIds.length > 0 && ` • ${brandIds.length} brand(s)`}
              {clientIds.length > 0 && ` • ${clientIds.length} client(s)`}
            </div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={950}
      centered
      destroyOnClose
      zIndex={1050}
      styles={{ mask: { zIndex: 1049 } }}
    >
      <Spin spinning={loading}>
        <div style={{ padding: "16px 0" }}>
          {/* Date Range Info - matches dashboard filter */}
          <div
            style={{
              marginBottom: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px",
              background: "#f5f5f5",
              borderRadius: "8px",
            }}
          >
            <span style={{ color: "#595959", fontSize: 13, fontWeight: 500 }}>
              Date Range: {dateRange.startDate} to {dateRange.endDate}
            </span>
            <span style={{ color: "#8c8c8c", fontSize: 12 }}>
              {brandIds.length} brand(s){clientIds && clientIds.length > 0 ? ` • ${clientIds.length} client(s)` : ""}
            </span>
          </div>

          {/* Error State */}
          {error && !loading && (
            <Empty description={error} style={{ padding: "60px 0" }} />
          )}

          {/* Summary Cards */}
          {!error && chartData && chartData.series && renderSummaryCards()}

          {/* Chart */}
          {!error &&
            chartData &&
            chartData.series &&
            (() => {
              // Process series data based on chart type
              let processedSeries = null;
              const chartType =
                chartData.type === "donut"
                  ? "donut"
                  : chartData.type === "bar"
                  ? "bar"
                  : "area";

              try {
                if (chartData.type === "donut") {
                  // For donut: series must be a simple array of numbers [100, 200, 300]
                  const rawSeries = chartData.series;

                  if (!rawSeries || !Array.isArray(rawSeries)) {
                    processedSeries = [0];
                  } else if (rawSeries.length === 0) {
                    processedSeries = [0];
                  } else {
                    // Extract numbers from array
                    processedSeries = rawSeries
                      .map((item) => {
                        // If it's already a number, use it
                        if (typeof item === "number") return item;
                        // If it's an object with a value, extract it
                        if (typeof item === "object" && item !== null) {
                          if ("y" in item)
                            return typeof item.y === "number" ? item.y : 0;
                          if ("value" in item)
                            return typeof item.value === "number"
                              ? item.value
                              : 0;
                          if ("data" in item && Array.isArray(item.data))
                            return item.data[0] || 0;
                        }
                        // Try to parse as number
                        const num = parseFloat(item);
                        return isNaN(num) ? 0 : num;
                      })
                      .filter((val) => val > 0); // Remove zeros

                    // If no valid data, use placeholder
                    if (processedSeries.length === 0) {
                      processedSeries = [0];
                    }
                  }
                } else {
                  // For area/bar/line: series is array of objects [{name: "Series", data: [1,2,3]}]
                  if (Array.isArray(chartData.series)) {
                    processedSeries = chartData.series;
                  } else {
                    processedSeries = [];
                  }
                }
              } catch (err) {
                console.error("Error processing series:", err, chartData);
                processedSeries = chartData.type === "donut" ? [0] : [];
              }

              // Don't render if we don't have valid data
              if (
                !processedSeries ||
                (Array.isArray(processedSeries) && processedSeries.length === 0)
              ) {
                return (
                  <Empty
                    description="No data available for this chart"
                    style={{ padding: "60px 0" }}
                  />
                );
              }

              return (
                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 12,
                    padding: 24,
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <ReactApexChart
                    key={`${chartType}-${dateRange.startDate}-${dateRange.endDate}-${JSON.stringify(
                      processedSeries
                    ).slice(0, 100)}`}
                    options={getChartOptions()}
                    series={processedSeries}
                    type={chartType}
                    height={400}
                  />
                </div>
              );
            })()}

          {/* No brands selected */}
          {!loading && brandIds.length === 0 && (
            <Empty
              description="Please select at least one brand to view chart data"
              style={{ padding: "60px 0" }}
            />
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default ChartModal;
